/**
 * Custom Validators
 *
 * Centralised custom validation functions for use with Yup's .test() method.
 * These validators are designed to be v1.x-compatible.
 *
 * In Yup v1.x, the .test() callback signature has changed:
 * - v0.27: .test(name, message, function(value) { this.path, this.parent... })
 * - v1.x:  .test(name, message, function(value, context) { context.path, context.parent... })
 *
 * These validators use arrow functions that work with both versions
 * when the context is not needed, or use the explicit context parameter.
 */
import { isPossiblePhoneNumber } from "react-phone-number-input";
import * as Yup from "yup";
import type { CustomTestOptions } from "./types";

/**
 * Validates a phone number if it exists.
 * Returns true if the phone number is empty/null, or if it's a valid phone number.
 *
 * @param phoneNumber - The phone number string to validate
 * @returns true if valid or empty, false if invalid
 *
 * @example
 * // In a validation schema:
 * phoneNumber: optionalString().test(
 *   "isValidPhone",
 *   "Invalid phone number",
 *   ifPhoneNumberExistsIsValid
 * )
 */
export const ifPhoneNumberExistsIsValid = (phoneNumber: string | undefined | null): boolean => {
  if (phoneNumber) {
    return isPossiblePhoneNumber(phoneNumber);
  }
  return true;
};

/**
 * Creates a phone number validation test configuration.
 * Use this with Yup's .test() method.
 *
 * @param message - Custom error message (defaults to "Invalid phone number")
 * @returns CustomTestOptions configuration object
 *
 * @example
 * phoneNumber: optionalString().test(phoneNumberTest())
 * phoneNumber: optionalString().test(phoneNumberTest("Please enter a valid phone number"))
 */
export const phoneNumberTest = (
  message: string = "Invalid phone number"
): CustomTestOptions<string> => ({
  name: "isValidPhoneNumber",
  message,
  test: ifPhoneNumberExistsIsValid,
});

/**
 * Creates a Yup string schema with phone number validation.
 * Convenience method that combines optionalString with phone validation.
 *
 * @param message - Custom error message (defaults to "Invalid phone number")
 * @returns Yup StringSchema with phone number validation
 *
 * @example
 * mobilePhone: phoneNumberField()
 * workPhone: phoneNumberField("Please provide a valid work phone number")
 */
export const phoneNumberField = (
  message: string = "Invalid phone number"
): Yup.Schema<string | null | undefined> => {
  return Yup.string().nullable().test("isValidPhoneNumber", message, ifPhoneNumberExistsIsValid);
};

/**
 * Validates that a URL is properly formatted if provided.
 * Returns true if the URL is empty/null, or if it's a valid URL.
 *
 * @param url - The URL string to validate
 * @returns true if valid or empty, false if invalid
 *
 * @example
 * website: optionalString().test(
 *   "isValidUrl",
 *   "Invalid URL format",
 *   ifUrlExistsIsValid
 * )
 */
export const ifUrlExistsIsValid = (url: string | undefined | null): boolean => {
  if (!url) {
    return true;
  }
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Creates a URL validation test configuration.
 *
 * @param message - Custom error message
 * @returns CustomTestOptions configuration object
 *
 * @example
 * website: optionalString().test(urlTest())
 */
export const urlTest = (message: string = "Invalid URL format"): CustomTestOptions<string> => ({
  name: "isValidUrl",
  message,
  test: ifUrlExistsIsValid,
});

/**
 * Validates that a value matches a specific pattern if provided.
 *
 * @param pattern - RegExp pattern to match
 * @param value - The string value to validate
 * @returns true if valid or empty, false if invalid
 *
 * @example
 * const isAlphanumeric = createPatternValidator(/^[a-zA-Z0-9]+$/);
 * code: optionalString().test("isAlphanumeric", "Must be alphanumeric", isAlphanumeric)
 */
export const createPatternValidator = (pattern: RegExp) => {
  return (value: string | undefined | null): boolean => {
    if (!value) {
      return true;
    }
    return pattern.test(value);
  };
};

/**
 * Validates that a number is within a specific range if provided.
 *
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (inclusive)
 * @returns Validation function
 *
 * @example
 * const isPercentage = createRangeValidator(0, 100);
 * percentage: optionalNumber().test("isPercentage", "Must be between 0 and 100", isPercentage)
 */
export const createRangeValidator = (min: number, max: number) => {
  return (value: number | undefined | null): boolean => {
    if (value === null || value === undefined) {
      return true;
    }
    return value >= min && value <= max;
  };
};

/**
 * Validates that a string doesn't contain only whitespace.
 *
 * @param value - The string value to validate
 * @returns true if has non-whitespace content or is empty, false if only whitespace
 *
 * @example
 * name: requiredString("Name is required").test(
 *   "notOnlyWhitespace",
 *   "Cannot be only whitespace",
 *   notOnlyWhitespace
 * )
 */
export const notOnlyWhitespace = (value: string | undefined | null): boolean => {
  if (!value) {
    return true;
  }
  return value.trim().length > 0;
};

/**
 * Creates a custom test configuration for not allowing only whitespace.
 *
 * @param message - Custom error message
 * @returns CustomTestOptions configuration object
 */
export const notOnlyWhitespaceTest = (
  message: string = "Cannot contain only whitespace"
): CustomTestOptions<string> => ({
  name: "notOnlyWhitespace",
  message,
  test: notOnlyWhitespace,
});

// ============================================================================
// File Validation Utilities
// ============================================================================

/**
 * File item structure for file upload validation.
 */
export interface FileItem {
  fileName?: string;
  file?: { size: number };
}

/**
 * File type configuration for accepted file types.
 */
export interface FileTypeConfig {
  extension: string;
  format?: string;
  mimeType?: string;
}

/**
 * Validates file types against an accepted list.
 *
 * @param acceptedTypes - Array of accepted file type configurations
 * @returns Validation function for file type checking
 *
 * @example
 * const validateImageTypes = createFileTypeValidator([
 *   { extension: ".jpg" },
 *   { extension: ".png" },
 *   { extension: ".gif" }
 * ]);
 */
export const createFileTypeValidator = (acceptedTypes: FileTypeConfig[]) => {
  return (value: FileItem[] | undefined | null): boolean => {
    if (!Array.isArray(value) || value.length === 0) return false;

    return value.every((item) => {
      if (!item?.fileName || typeof item.fileName !== "string") return false;
      const parts = item.fileName.split(".");
      if (!parts || parts.length <= 1) return false;
      const ext = "." + parts[parts.length - 1].toLowerCase();
      return acceptedTypes.some((f) => f.extension.toLowerCase() === ext);
    });
  };
};

/**
 * Creates a file type validation test configuration.
 *
 * @param acceptedTypes - Array of accepted file type configurations
 * @param message - Custom error message
 * @returns CustomTestOptions for file type validation
 *
 * @example
 * files: Yup.mixed().test(fileTypeTest(acceptedFileTypes, "Invalid file type"))
 */
export const fileTypeTest = (
  acceptedTypes: FileTypeConfig[],
  message: string = "Not a valid file type"
): CustomTestOptions<FileItem[]> => ({
  name: "is-valid-type",
  message,
  test: createFileTypeValidator(acceptedTypes),
});

/**
 * Validates that total file size doesn't exceed a maximum.
 *
 * @param maxSizeBytes - Maximum allowed total size in bytes
 * @returns Validation function for file size checking
 *
 * @example
 * const validateMaxSize = createFileSizeValidator(500_000_000); // 500MB
 */
export const createFileSizeValidator = (maxSizeBytes: number) => {
  return (value: FileItem[] | undefined | null): boolean => {
    if (!Array.isArray(value) || value.length === 0) return false;

    let totalSize = 0;
    for (const item of value) {
      if (!item.file?.size) return false;
      totalSize += item.file.size;
    }
    return totalSize <= maxSizeBytes;
  };
};

/**
 * Creates a file size validation test configuration.
 *
 * @param maxSizeBytes - Maximum allowed total size in bytes
 * @param message - Custom error message (defaults to include MB limit)
 * @returns CustomTestOptions for file size validation
 *
 * @example
 * files: Yup.mixed().test(fileSizeTest(500_000_000))
 */
export const fileSizeTest = (
  maxSizeBytes: number,
  message?: string
): CustomTestOptions<FileItem[]> => ({
  name: "is-valid-size",
  message: message ?? `Max allowed size is ${Math.round(maxSizeBytes / 1_000_000)}MB`,
  test: createFileSizeValidator(maxSizeBytes),
});

/**
 * Options for creating a file upload field schema.
 */
export interface FileUploadFieldOptions {
  /** Whether the file upload is required */
  required?: boolean;
  /** Error message when file is required but missing */
  requiredMessage?: string;
  /** Accepted file types */
  acceptedTypes?: FileTypeConfig[];
  /** Error message for invalid file type */
  typeMessage?: string;
  /** Maximum total file size in bytes */
  maxSizeBytes?: number;
  /** Error message for exceeding size limit */
  sizeMessage?: string;
}

/**
 * Creates a complete file upload field schema with type and size validation.
 *
 * @param options - Configuration options for file upload validation
 * @returns Yup MixedSchema with file validations applied
 *
 * @example
 * // Basic required file upload with type and size validation
 * files: fileUploadField({
 *   required: true,
 *   requiredMessage: "Please upload a file",
 *   acceptedTypes: [{ extension: ".pdf" }, { extension: ".doc" }],
 *   maxSizeBytes: 500_000_000, // 500MB
 * })
 */
export const fileUploadField = (
  options: FileUploadFieldOptions
): Yup.Schema<FileItem[] | null | undefined> => {
  // In Yup v1.x, .nullable() must come BEFORE .required() for correct behaviour
  let schema = Yup.mixed<FileItem[]>().nullable();

  if (options.required) {
    schema = schema.required(options.requiredMessage ?? "Please upload a file");
  }

  if (options.acceptedTypes) {
    const typeValidator = createFileTypeValidator(options.acceptedTypes);
    schema = schema.test(
      "is-valid-type",
      options.typeMessage ?? "Not a valid file type",
      typeValidator
    );
  }

  if (options.maxSizeBytes) {
    const sizeValidator = createFileSizeValidator(options.maxSizeBytes);
    schema = schema.test(
      "is-valid-size",
      options.sizeMessage ??
        `Max allowed size is ${Math.round(options.maxSizeBytes / 1_000_000)}MB`,
      sizeValidator
    );
  }

  return schema as Yup.Schema<FileItem[] | null | undefined>;
};

// ============================================================================
// Prefix/Pattern Validation Utilities
// ============================================================================

/**
 * Options for prefix validation.
 */
export interface StartsWithOptions {
  /** Whether comparison should be case-sensitive (default: true) */
  caseSensitive?: boolean;
}

/**
 * Creates a validator that checks if a string starts with a specific prefix.
 * Returns true if value is empty/null (optional validation pattern).
 *
 * @param prefix - The prefix the string must start with
 * @param options - Optional configuration
 * @returns Validation function
 *
 * @example
 * const startsWithP = createStartsWithValidator("P");
 * projectNumber: Yup.string().test("prefix", "Must start with P", startsWithP)
 */
export const createStartsWithValidator = (prefix: string, options?: StartsWithOptions) => {
  return (value: string | undefined | null): boolean => {
    if (!value) return true;
    const compareValue = options?.caseSensitive === false ? value.toUpperCase() : value;
    const comparePrefix = options?.caseSensitive === false ? prefix.toUpperCase() : prefix;
    return compareValue.startsWith(comparePrefix);
  };
};

/**
 * Creates a test configuration for prefix validation.
 *
 * @param prefix - The prefix the string must start with
 * @param fieldName - Human-readable field name for error message
 * @param options - Optional configuration
 * @returns CustomTestOptions for prefix validation
 *
 * @example
 * projectNumber: Yup.string().nullable().test(startsWithTest("P", "Project Number"))
 */
export const startsWithTest = (
  prefix: string,
  fieldName: string,
  options?: StartsWithOptions
): CustomTestOptions<string> => ({
  name: `starts-with-${prefix.toLowerCase()}`,
  message: `${fieldName} must start with "${prefix}"`,
  test: createStartsWithValidator(prefix, options),
});

/**
 * Creates a Yup string schema with prefix validation.
 *
 * @param prefix - The prefix the string must start with
 * @param fieldName - Human-readable field name for error message
 * @param options - Optional configuration
 * @returns Yup StringSchema with prefix validation
 *
 * @example
 * projectNumber: startsWithField("P", "Project Number")
 * contractNumber: startsWithField("C", "Contract Number")
 */
export const startsWithField = (
  prefix: string,
  fieldName: string,
  options?: StartsWithOptions
): Yup.Schema<string | null | undefined> => {
  const validator = createStartsWithValidator(prefix, options);
  return Yup.string()
    .nullable()
    .test(
      `starts-with-${prefix.toLowerCase()}`,
      `${fieldName} must start with "${prefix}"`,
      validator
    );
};

// ============================================================================
// Context-Based Validation Utilities (Yup v1.x Compatible)
// ============================================================================

/**
 * Type for context-aware test function.
 * Uses the Yup v1.x compatible signature with explicit this context.
 */
export type ContextAwareTestFunction<TValue, TContext = Record<string, unknown>> = (
  this: Yup.TestContext,
  value: TValue | undefined | null
) => boolean;

/**
 * Creates a test that validates based on context values.
 * Uses `function` syntax to access `this.options.context`.
 *
 * @param name - Unique test name
 * @param message - Error message on failure
 * @param testFn - Test function that receives value and has access to this.options.context
 * @returns Test configuration for use with Yup .test()
 *
 * @example
 * // Validate based on context value
 * field: Yup.string().test(createContextTest(
 *   "requiresPermission",
 *   "You don't have permission",
 *   function(value) {
 *     const ctx = this.options.context as { hasPermission?: boolean };
 *     return ctx?.hasPermission === true;
 *   }
 * ))
 */
export const createContextTest = <TValue>(
  name: string,
  message: string,
  testFn: ContextAwareTestFunction<TValue>
): { name: string; message: string; test: ContextAwareTestFunction<TValue> } => ({
  name,
  message,
  test: testFn,
});

/**
 * Creates a test that validates based on sibling field values.
 * Uses `function` syntax to access `this.parent`.
 *
 * @param name - Unique test name
 * @param message - Error message on failure
 * @param testFn - Test function that has access to this.parent
 * @returns Test configuration for use with Yup .test()
 *
 * @example
 * // Validate that confirmPassword matches password
 * confirmPassword: Yup.string().test(createParentTest(
 *   "passwords-match",
 *   "Passwords must match",
 *   function(value) {
 *     return this.parent.password === value;
 *   }
 * ))
 */
export const createParentTest = <TValue>(
  name: string,
  message: string,
  testFn: ContextAwareTestFunction<TValue>
): { name: string; message: string; test: ContextAwareTestFunction<TValue> } => ({
  name,
  message,
  test: testFn,
});

/**
 * Password match validation test.
 * Validates that a confirmation password matches the original password field.
 *
 * @param passwordFieldName - Name of the password field to compare against
 * @param message - Custom error message
 * @returns Test configuration for password matching
 *
 * @example
 * confirmPassword: Yup.string().nullable().test(passwordsMatchTest("newPassword"))
 */
export const passwordsMatchTest = (
  passwordFieldName: string = "password",
  message: string = "Passwords must match"
) => ({
  name: "passwords-match",
  message,
  test: function (this: Yup.TestContext, value: string | undefined | null): boolean {
    const parent = this.parent as Record<string, unknown>;
    const password = parent?.[passwordFieldName];
    // If no password set yet, or no confirm value, allow it
    if (!password || !value) return true;
    return password === value;
  },
});

/**
 * Conditional required validation based on another field's value.
 *
 * @param dependsOnField - Field name that determines if this field is required
 * @param condition - Function to check if requirement applies
 * @param message - Error message when required but missing
 * @returns Test configuration for conditional requirement
 *
 * @example
 * // Require reasonCode when workflow requires it
 * reasonCodeID: Yup.number().nullable().test(conditionalRequiredTest(
 *   "workflowRequiresReasonCode",
 *   (parent) => parent.workflowRequiresReasonCode === true,
 *   "Please provide a reason code"
 * ))
 */
export const conditionalRequiredTest = <TParent extends Record<string, unknown>>(
  name: string,
  condition: (parent: TParent) => boolean,
  message: string
) => ({
  name,
  message,
  test: function (this: Yup.TestContext, value: unknown): boolean {
    const parent = this.parent as TParent;
    if (condition(parent)) {
      return value != null && value !== "";
    }
    return true;
  },
});
